import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Flex } from '@chakra-ui/react';

import { ApplicationState } from '../../../../store';
import {
  updateConflict,
  saveMessage,
  updateUniStudentNumber,
  updateOfferEvidence,
} from '../../../../store/student/actions';

import { all } from './fieldData';
import Field from './field';
import { IMessage, InfoState, InfoField } from 'types';
import UpdateConflictModal from '../../modals/updateConflict';
import IntakeChangeConflictModal from '../../modals/intakeChangeConflictModal';
import EditUniStudentNumberModal from '../../modals/editUniStudentNumber';
import EditOfferEvidenceModal from '../../modals/editOfferEvidence';
import CASBypassWarning from '../../../molecules/casBypassWarning/casBypassWarning';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { useAuth } from '../../../../hooks/useAuth';

import { Section } from './Section';

interface Props {
  id: string;
}

function Info(props: Props): React.ReactElement {
  const dispatch = useDispatch();
  const params = useParams<{ uni: string; id: string }>();

  const [editUniPartnerId, setEditUniPartnerId] = useState(false);
  const [editOfferEvidence, setEditOfferEvidence] = useState(false);
  const [editCourseIndex, setEditCourseIndex] = useState(0);

  const { navigateTo } = useCustomNavigate();
  const { isAgent } = useAuth();
  const onEdit = (page: string, field: InfoField): void => {
    switch (page) {
      case 'edit-uni-partner-id-modal':
        setEditUniPartnerId(true);
        break;
      case 'edit-offer-evidence-modal':
        setEditOfferEvidence(true);
        setEditCourseIndex(field.index ?? 0);
        break;
      default:
        navigateTo(`${props.id}/${page}`);
    }
  };

  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const config = useSelector(({ config }: ApplicationState) => config, shallowEqual);

  const fields = useMemo(() => all(student, student.config, config), [student, config]);

  const isCasBypass = useSelector(({ student }: ApplicationState) => student.offer.casBypass);

  const { isCourseConflict } = student.offer;

  const [showIntakeChangeError, setShowIntakeChangeError] = useState(false);
  const [selectedData, setSelectedData] = useState<{
    fieldId: string;
    fieldIndex?: number;
    optionId: string;
  } | null>(null);

  const onUpdateUniStudentNumber = (value: string): void => {
    dispatch(updateUniStudentNumber(params.id ?? '', value));
  };

  const onUpdateOfferEvidence = (value: string): void => {
    dispatch(updateOfferEvidence(editCourseIndex, value));
  };

  const onSubmitConflictOption = ({
    fieldId,
    fieldIndex,
    optionId,
    message,
  }: {
    fieldId: string;
    fieldIndex?: number;
    optionId: string;
    message: IMessage;
  }): void => {
    dispatch(
      updateConflict({
        id: fieldId,
        index: fieldIndex,
        option: optionId,
      }),
    );
    dispatch(saveMessage(message));
  };

  const onCourseRelease = (): void => {
    setSelectedData({
      fieldId: 'courses',
      optionId: 'release',
    });
  };

  // put removed courses last
  const sortInfoFieldFn = (val1: InfoField[], val2: InfoField[]): number => {
    const val1HasRemovedCourse = val1.some(f => f.state === InfoState.Removed);
    const val2HasRemovedCourse = val2.some(f => f.state === InfoState.Removed);
    if (val1HasRemovedCourse) return 1;
    if (val2HasRemovedCourse) return -1;
    return 0;
  };

  const sortedCourses = [...fields.courses].sort(sortInfoFieldFn);

  const getModal = (): React.ReactNode => {
    if (editUniPartnerId) {
      return (
        <EditUniStudentNumberModal
          uniStudentNumber={student.core.uniPartnerId}
          onUpdate={value => onUpdateUniStudentNumber(value)}
          onClose={() => setEditUniPartnerId(false)}
        />
      );
    }
    if (editOfferEvidence) {
      let offerEvidence = '';
      const offerEvidenceField = fields.courses
        .find(x => x.some(f => f.index === editCourseIndex))
        ?.find(f => f.id === 'courseOfferEvidence');
      switch (offerEvidenceField?.state) {
        case InfoState.NotFilledImport:
          offerEvidence = offerEvidenceField?.values?.import ?? '';
          break;
        default:
          offerEvidence = offerEvidenceField?.values?.student ?? '';
          break;
      }
      return (
        <EditOfferEvidenceModal
          offerEvidence={offerEvidence}
          onUpdate={value => onUpdateOfferEvidence(value)}
          onClose={() => setEditOfferEvidence(false)}
        />
      );
    }
    return null;
  };
  return (
    <>
      {isCasBypass ? (
        <CASBypassWarning />
      ) : (
        <>
          <>
            {showIntakeChangeError ? (
              <IntakeChangeConflictModal onClose={() => setShowIntakeChangeError(false)} />
            ) : null}
            {selectedData !== null ? (
              <UpdateConflictModal
                fieldId={selectedData.fieldId}
                optionId={selectedData.optionId}
                hideSendToStudent={fields.agentDetails.some(f => f.id === selectedData.fieldId)}
                onSubmit={message =>
                  onSubmitConflictOption({
                    fieldId: selectedData.fieldId,
                    fieldIndex: selectedData.fieldIndex,
                    optionId: selectedData.optionId,
                    message,
                  })
                }
                onClose={() => setSelectedData(null)}
              />
            ) : null}

            <Section heading="Personal Information">
              {fields.personalInfo.map(field => {
                return (
                  <Field
                    key={field.id}
                    data={field}
                    onEdit={onEdit}
                    onConflictOption={optionId =>
                      setSelectedData({
                        fieldId: field.id,
                        fieldIndex: field.index,
                        optionId,
                      })
                    }
                  />
                );
              })}
            </Section>

            {sortedCourses.map((fieldSet, i) => {
              const isCourseRemoved = fieldSet.some(f => f.state === InfoState.Removed);
              return (
                <Section
                  heading={
                    i === 0 ? (
                      <Flex>
                        Course Information {isCourseRemoved ? '(removed)' : null}
                        {isCourseConflict && config.enableStudentCourseBlock === true ? (
                          <Button size="sm" variant={'ghost'} onClick={onCourseRelease} ml={'auto'}>
                            Conflicts resolved - release student
                          </Button>
                        ) : null}
                      </Flex>
                    ) : (
                      <>
                        Course Information{` (${i + 1})`} {isCourseRemoved ? '(removed)' : null}
                      </>
                    )
                  }
                  key={i}
                >
                  {fieldSet.map(field => {
                    return (
                      <Field
                        key={field.id}
                        data={field}
                        onEdit={onEdit}
                        onConflictOption={optionId => {
                          return setSelectedData({
                            fieldId: field.id,
                            fieldIndex: field.index,
                            optionId,
                          });
                        }}
                      />
                    );
                  })}
                </Section>
              );
            })}

            <Section heading="Deposit">
              {fields.deposit.map(field => {
                return (
                  <Field
                    key={field.id}
                    data={field}
                    onEdit={onEdit}
                    onConflictOption={optionId =>
                      setSelectedData({
                        fieldId: field.id,
                        fieldIndex: field.index,
                        optionId,
                      })
                    }
                  />
                );
              })}
            </Section>

            {fields.agentDetails.length > 0 && (
              <Section heading="Agent Details">
                {fields.agentDetails
                  .filter(field => !(isAgent && field.id === 'agentDisplayName'))
                  .map(field => (
                    <Field
                      key={field.id}
                      data={field}
                      onEdit={onEdit}
                      onConflictOption={optionId =>
                        setSelectedData({
                          fieldId: field.id,
                          fieldIndex: field.index,
                          optionId,
                        })
                      }
                    />
                  ))}
              </Section>
            )}
          </>
        </>
      )}

      {getModal()}
    </>
  );
}

export default Info;
